import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./Icon";

/** The prototype's info note (its `info()`): the reason behind something sits behind a small i, not in the body. */
export function Info({ children, label = "Why" }: { children: ReactNode; label?: string }) {
  const id = useId();
  const wrap = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const [place, setPlace] = useState<CSSProperties | null>(null);

  useEffect(() => {
    if (!place) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setPlace(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPlace(null);
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [place]);

  const toggle = () => {
    const b = btn.current;
    const w0 = wrap.current;
    if (place || !b || !w0) {
      setPlace(null);
      return;
    }
    // Why: keep the note inside the screen on phones, with its pointer still under the i.
    const vw = document.documentElement.clientWidth;
    const box = b.closest(".sheet");
    const lim = box ? box.getBoundingClientRect() : { left: 0, right: vw };
    const br = b.getBoundingClientRect();
    const wr = w0.getBoundingClientRect();
    const w = Math.min(320, lim.right - lim.left - 32);
    const cx = br.left + br.width / 2;
    const left = Math.max(lim.left + 16, Math.min(lim.right - 16 - w, cx - 22));
    setPlace({ width: w, left: left - wr.left, ["--ax" as string]: `${cx - left}px` });
  };

  return (
    <span className="info" ref={wrap}>
      <button
        ref={btn}
        type="button"
        className="infobtn"
        aria-label={label}
        aria-expanded={place !== null}
        aria-controls={id}
        onClick={toggle}
      >
        <Icon name="info" />
      </button>
      <span className="infotip" id={id} role="note" hidden={place === null} style={place ?? undefined}>
        {children}
      </span>
    </span>
  );
}
